import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Asset_Status_Enum,
  InsertAssetMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertAssetDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface InsertAssetInput {
  Title: string;
  Status: Asset_Status_Enum;
  Description?: null | string;
  AssetType?: null | string;
  SerialNumber?: null | string;
  PurchaseDate?: null | string;
  Location?: null | string;
  Cost?: null | number;
  DepreciationMethod?: null | string;
  Assignee?: null | string;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
}

export const useInsertAsset = createMutationHook({
  name: 'insertAsset',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.asset.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertAssetInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.asset.register.queryKey(),
    trpc.frontend.asset.assetById.queryKey(),
  ],
  mapResponse: (result: { Id: string }): InsertAssetMutation => ({
    insertChildAsset: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertAssetDocument,
  graphqlVariables: (input: InsertAssetInput) => ({ ...input }),
  // Parity with the pre-migration wrapper: the Apollo path threw when the
  // response carried no Id, so a missing Id never silently reached consumers.
  validateGraphqlResponse: (data) => {
    if (!data.insertChildAsset?.Id) {
      throw new Error('Failed to insert asset');
    }
  },
  evictFields: ['asset', 'asset_aggregate'],
  refetchQueries: [namedOperations.Query.getAssetById],
});
